import { Head } from "@inertiajs/react"
import { Mic, MicOff, PenLine, PhoneOff, ScreenShare, ScreenShareOff, Settings2, Video, VideoOff } from "lucide-react"
import { useState } from "react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Select } from "@/components/ui/select"
import { cn } from "@/lib/utils"
import { BACKGROUND_SCENES, type BackgroundMode } from "@/lib/video/background"
import { useVideoSession, type ConnectionState, type VideoSessionState } from "@/lib/video/use-video-session"
import { VideoView } from "@/lib/video/VideoView"
import { Whiteboard } from "@/lib/video/Whiteboard"

type Props = {
  room_id: string
  token: string
  expires_at: string
  signaling_url: string | null
}

const TITLE = "Video session | Sanctuary Therapy Centre"
const DESCRIPTION =
  "A private, encrypted video session with Sanctuary Therapy Centre. Runs in your browser — nothing to install, nothing recorded."

function SessionShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen flex-col bg-cream text-ink-body">
      <header className="border-b border-hairline bg-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-3 sm:px-6">
          <div className="flex min-w-0 items-center gap-3">
            <img src="/sanctuary-favicon.png" alt="" className="h-8 w-8 shrink-0 rounded-full object-cover" />
            <span className="truncate font-display text-lg text-teal">Sanctuary Therapy Centre</span>
          </div>
          <Badge tone="muted" className="shrink-0 whitespace-nowrap">
            <span className="sm:hidden">Not recorded</span>
            <span className="hidden sm:inline">Not recorded · encrypted</span>
          </Badge>
        </div>
      </header>
      <main className="flex flex-1 flex-col">{children}</main>
      <footer className="border-t border-hairline px-4 py-4 text-center text-xs text-ink-muted sm:px-6">
        Sessions are never recorded. Video and audio travel encrypted, directly between you and your
        therapist, and never pass through a third-party video service.
      </footer>
    </div>
  )
}

// The hint names what was asked for: "Blur" for blur, the scene's label for
// a scene — a "Blur is on" under a garden window would read as a bug.
function backgroundHint(state: VideoSessionState): string {
  const what = state.background === "blur" ? "Blur" : "Background"
  switch (state.backgroundStatus) {
    case "off": return ""
    case "loading": return `Preparing ${what.toLowerCase()}…`
    case "native": return "Blur is on (provided by your device)."
    case "software": return `${what} is on.`
    case "unsupported": return `${what} isn't available in this browser.`
    case "too-slow": return `${what} was turned off — this device couldn't keep up.`
  }
}

function DevicePickers({
  state,
  onSwitch,
  onBackground,
}: {
  state: VideoSessionState
  onSwitch: (kind: "audio" | "video", id: string) => void
  onBackground: (mode: BackgroundMode) => void
}) {
  return (
    <div className="grid gap-4 sm:grid-cols-3">
      <div>
        <label htmlFor="camSel">Camera</label>
        <Select
          id="camSel"
          className="mt-1"
          value={state.camId}
          disabled={!state.mediaReady}
          onChange={(e) => onSwitch("video", e.target.value)}
        >
          {state.devices.cams.map((d, i) => (
            <option key={d.deviceId || i} value={d.deviceId}>
              {d.label || `Camera ${i + 1}`}
            </option>
          ))}
        </Select>
      </div>
      <div>
        <label htmlFor="micSel">Microphone</label>
        <Select
          id="micSel"
          className="mt-1"
          value={state.micId}
          disabled={!state.mediaReady}
          onChange={(e) => onSwitch("audio", e.target.value)}
        >
          {state.devices.mics.map((d, i) => (
            <option key={d.deviceId || i} value={d.deviceId}>
              {d.label || `Microphone ${i + 1}`}
            </option>
          ))}
        </Select>
      </div>
      <div>
        <label htmlFor="bgSel">Background</label>
        <Select
          id="bgSel"
          className="mt-1"
          value={state.background}
          disabled={!state.mediaReady || state.backgroundStatus === "loading"}
          onChange={(e) => onBackground(e.target.value as BackgroundMode)}
        >
          <option value="none">None</option>
          <option value="blur">Blur</option>
          {BACKGROUND_SCENES.map((scene) => (
            <option key={scene.key} value={`scene:${scene.key}`}>
              {scene.label}
            </option>
          ))}
        </Select>
        <p className="mt-1 text-sm text-ink-muted" aria-live="polite" data-background-status={state.backgroundStatus}>
          {backgroundHint(state)}
        </p>
      </div>
    </div>
  )
}

const CONNECTION_MESSAGE: Record<ConnectionState, string> = {
  idle: "Connecting to the other participant…",
  new: "Connecting to the other participant…",
  connecting: "Connecting to the other participant…",
  connected: "",
  disconnected: "Connection interrupted — reconnecting…",
  failed: "Couldn't connect to the other participant. Retrying…",
  closed: "The connection has closed.",
}

// Where a host who backs out ends up. A tab a script opened (the admin's
// "Open your host link" is target=_blank) may close itself; one the address
// bar opened may not, and window.close() is then a silent no-op — so wait a
// beat and, if the page is still here, go back to the admin instead.
function leaveToAdmin() {
  window.close()
  window.setTimeout(() => {
    if (!window.closed) window.location.assign("/admin/video-sessions/new")
  }, 150)
}

export default function VideoSessionShow({ room_id, token, signaling_url }: Props) {
  const {
    state,
    join,
    admit,
    decline,
    toggleMic,
    toggleCam,
    switchDevice,
    setBackground,
    cancel,
    startScreenShare,
    stopScreenShare,
    openWhiteboard,
    closeWhiteboard,
    attachWhiteboardCanvas,
    retry,
    end,
  } = useVideoSession({
    roomId: room_id,
    token,
    signalingUrl: signaling_url,
  })
  const [showDevices, setShowDevices] = useState(false)
  const isHost = state.role === "host"

  return (
    <>
      <Head title={TITLE}>
        <meta name="description" content={DESCRIPTION} />
        <meta property="og:title" content={TITLE} />
        <meta property="og:description" content={DESCRIPTION} />
        <meta name="robots" content="noindex, nofollow" />
      </Head>

      <SessionShell>
        {state.banner && (
          <div role="status" className="callout callout-signal mx-auto mt-4 w-full max-w-3xl py-3 text-center">
            {state.banner}
          </div>
        )}

        {/* ---- Lobby: preview + device choice ---- */}
        {state.stage === "lobby" && (
          <section className="mx-auto w-full max-w-3xl px-4 py-10 sm:px-6">
            <h1>Video session</h1>
            <p className="mt-2 text-ink-muted">
              {state.role === null
                ? "Getting things ready…"
                : isHost
                  ? "You are hosting this session. Your client will wait until you let them in."
                  : "Your therapist has invited you to a video session. Check your camera and microphone, then ask to join."}
            </p>

            <div className="mt-6 rounded-2xl bg-white p-5 ring-1 ring-hairline sm:p-6">
              <div className="relative aspect-video overflow-hidden rounded-xl bg-black">
                <VideoView stream={state.localStream} muted mirror label="Your camera preview" />
              </div>

              {state.mediaStalled && !state.mediaError && (
                <div role="alert" className="callout callout-signal mt-4">
                  No camera prompt has appeared. On a phone this usually means the browser app
                  itself isn't allowed to use the camera: open your phone's Settings → Apps → your
                  browser → Permissions, allow Camera and Microphone, then reload this page. On a
                  computer, check the camera icon in the address bar.
                </div>
              )}
              {state.mediaError && (
                <div role="alert" className="callout callout-danger mt-4">
                  {typeof window !== "undefined" && !window.isSecureContext
                    ? "This page was opened over plain http, and browsers only offer the camera on https. Open the session link over https (or use localhost when developing)."
                    : "Camera or microphone access was blocked. Please allow access in your browser's address bar and reload this page."}
                </div>
              )}

              <div className="mt-5">
                <DevicePickers state={state} onSwitch={switchDevice} onBackground={setBackground} />
              </div>

              <div className="mt-6 flex flex-wrap items-center gap-3">
                <Button size="lg" onClick={join} disabled={!state.mediaReady || state.role === null}>
                  {isHost ? "Start session" : "Ask to join"}
                </Button>
                <Button
                  size="lg"
                  variant="secondary"
                  onClick={() => {
                    cancel()
                    if (isHost) leaveToAdmin()
                  }}
                >
                  Cancel
                </Button>
                {!state.mediaReady && !state.mediaError && !state.mediaStalled && (
                  <span className="text-sm text-ink-muted">Waiting for camera permission…</span>
                )}
              </div>
            </div>
          </section>
        )}

        {/* ---- Waiting room / admission ---- */}
        {state.stage === "waiting" && (
          <section className="mx-auto w-full max-w-3xl px-4 py-10 sm:px-6">
            <h1>{isHost ? "Waiting room" : "One moment"}</h1>
            <p className="mt-2 text-ink-muted">
              {isHost
                ? "Waiting for your client to arrive. You'll be asked to let them in."
                : "Waiting for your therapist to let you in. Please keep this page open."}
            </p>

            <div className="mt-6 grid gap-6 sm:grid-cols-[1fr_minmax(0,220px)]">
              <div className="callout">
                {isHost && state.knock ? (
                  <>
                    <p>
                      <strong>A client is waiting to join.</strong>
                    </p>
                    <div className="mt-4 flex flex-wrap gap-3">
                      <Button onClick={admit}>Admit</Button>
                      <Button variant="secondary" onClick={decline}>
                        Not now
                      </Button>
                    </div>
                  </>
                ) : (
                  <p className="text-ink-muted">
                    {state.socket === "connected"
                      ? isHost
                        ? "No one is waiting yet."
                        : "Your therapist has been notified."
                      : "Connecting…"}
                  </p>
                )}
              </div>
              <div className="relative aspect-video overflow-hidden rounded-xl bg-black sm:aspect-square">
                <VideoView stream={state.localStream} muted mirror label="Your camera preview" />
              </div>
            </div>

            {isHost && (
              <div className="mt-6">
                <Button
                  variant="secondary"
                  onClick={() => {
                    cancel()
                    leaveToAdmin()
                  }}
                >
                  Cancel session
                </Button>
              </div>
            )}
          </section>
        )}

        {/* ---- In call ---- */}
        {state.stage === "call" && (
          <section className="flex flex-1 flex-col bg-teal">
            <div className="relative mx-auto flex w-full max-w-6xl flex-1 flex-col px-4 py-4 sm:px-6">
              <div className="relative flex-1 overflow-hidden rounded-2xl bg-black" style={{ minHeight: "50vh" }}>
                {state.whiteboard ? (
                  <Whiteboard onReady={attachWhiteboardCanvas} onClose={closeWhiteboard} />
                ) : state.remoteScreenStream ? (
                  <VideoView
                    stream={state.remoteScreenStream}
                    label={
                      state.remoteScreenKind === "whiteboard"
                        ? "The whiteboard"
                        : "The other participant's shared screen"
                    }
                    className="object-contain"
                  />
                ) : (
                  <VideoView stream={state.remoteStream} label="The other participant" />
                )}
                {state.connection !== "connected" && (
                  <div
                    role="status"
                    className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/60 p-6 text-center"
                  >
                    <p className="text-white">{CONNECTION_MESSAGE[state.connection]}</p>
                    {state.slow && state.diagnostics && (
                      <p className="font-mono text-xs text-white/50" data-diagnostics>
                        ice {state.diagnostics.ice} · gathering {state.diagnostics.gathering} · signaling{" "}
                        {state.diagnostics.signaling} · local [{state.diagnostics.local.join(" ") || "none"}] ·
                        remote {state.diagnostics.remoteCandidates} cand · offer/answer{" "}
                        {state.diagnostics.remoteDescription ? "yes" : "no"} · pair{" "}
                        {state.diagnostics.selectedPair ?? "none"}
                      </p>
                    )}
                    {state.slow && state.diagnostics && state.diagnostics.local.length === 0 && (
                      <p className="max-w-md text-sm text-white/90">
                        This device isn't producing any network candidates, so it can't connect. That is
                        almost always a VPN, ad-blocker or "secure Wi-Fi" app running on the device, or a
                        private DNS setting — switch it off for the session and reload, or try another
                        network.
                      </p>
                    )}
                    {state.slow && state.connection !== "failed" && (
                      <p className="text-sm text-white/70">
                        This is taking longer than usual. Check that both devices are online.
                      </p>
                    )}
                    {(state.slow || state.connection === "failed") && (
                      <Button variant="secondary" onClick={retry}>
                        Retry connection
                      </Button>
                    )}
                  </div>
                )}
                {state.sharing && (
                  <div
                    role="status"
                    className="absolute left-3 top-3 rounded-full bg-black/60 px-3 py-1 text-xs text-white"
                  >
                    You're sharing your screen
                  </div>
                )}
                {/* Corner tiles. Bottom-left: the other participant's camera
                    while they present or while your whiteboard covers the
                    main view, or a preview of your own screen share — these
                    can't collide, since only one presentation runs at a
                    time. */}
                {(state.remoteScreenStream || state.whiteboard) && (
                  <div className="absolute bottom-3 left-3 aspect-video w-32 overflow-hidden rounded-lg ring-2 ring-white/40 sm:w-48">
                    <VideoView stream={state.remoteStream} label="The other participant" />
                  </div>
                )}
                {state.sharing && state.localScreenStream && (
                  <div className="absolute bottom-3 left-3 aspect-video w-32 overflow-hidden rounded-lg ring-2 ring-white/40 sm:w-48">
                    <VideoView
                      stream={state.localScreenStream}
                      muted
                      label="Your shared screen"
                      className="object-contain"
                    />
                  </div>
                )}
                <div className="absolute bottom-3 right-3 aspect-video w-32 overflow-hidden rounded-lg ring-2 ring-white/40 sm:w-48">
                  <VideoView stream={state.localStream} muted mirror label="Your camera" />
                </div>
              </div>

              <div className="mt-4 flex flex-wrap items-center justify-center gap-2 sm:gap-3">
                <Button
                  variant={state.micOn ? "secondary" : "danger"}
                  onClick={toggleMic}
                  aria-pressed={!state.micOn}
                  aria-label={state.micOn ? "Mute microphone" : "Unmute microphone"}
                >
                  {state.micOn ? <Mic className="h-4 w-4" /> : <MicOff className="h-4 w-4" />}
                  <span className="ml-2 hidden sm:inline">{state.micOn ? "Mute" : "Unmute"}</span>
                </Button>
                <Button
                  variant={state.camOn ? "secondary" : "danger"}
                  onClick={toggleCam}
                  aria-pressed={!state.camOn}
                  aria-label={state.camOn ? "Turn camera off" : "Turn camera on"}
                >
                  {state.camOn ? <Video className="h-4 w-4" /> : <VideoOff className="h-4 w-4" />}
                  <span className="ml-2 hidden sm:inline">{state.camOn ? "Camera off" : "Camera on"}</span>
                </Button>
                {isHost && state.canShare && (
                  <Button
                    variant={state.sharing ? "danger" : "secondary"}
                    onClick={() => (state.sharing ? stopScreenShare() : startScreenShare())}
                    aria-pressed={state.sharing}
                    aria-label={state.sharing ? "Stop sharing your screen" : "Share your screen"}
                  >
                    {state.sharing ? <ScreenShareOff className="h-4 w-4" /> : <ScreenShare className="h-4 w-4" />}
                    <span className="ml-2 hidden sm:inline">{state.sharing ? "Stop sharing" : "Share screen"}</span>
                  </Button>
                )}
                {isHost && (
                  <Button
                    variant={state.whiteboard ? "danger" : "secondary"}
                    onClick={() => (state.whiteboard ? closeWhiteboard() : openWhiteboard())}
                    aria-pressed={state.whiteboard}
                    aria-label={state.whiteboard ? "Close the whiteboard" : "Open the whiteboard"}
                  >
                    <PenLine className="h-4 w-4" />
                    <span className="ml-2 hidden sm:inline">{state.whiteboard ? "Close board" : "Whiteboard"}</span>
                  </Button>
                )}
                <Button
                  variant="secondary"
                  onClick={() => setShowDevices((v) => !v)}
                  aria-expanded={showDevices}
                  aria-label="Choose camera and microphone"
                >
                  <Settings2 className="h-4 w-4" />
                  <span className="ml-2 hidden sm:inline">Devices</span>
                </Button>
                <Button variant="danger" onClick={end}>
                  <PhoneOff className="h-4 w-4" />
                  <span className="ml-2">End session</span>
                </Button>
              </div>

              <div className={cn("mt-4 rounded-xl bg-white p-4", !showDevices && "hidden")}>
                <DevicePickers state={state} onSwitch={switchDevice} onBackground={setBackground} />
              </div>

              {isHost && state.devIce && (
                <p className="mt-3 text-center text-xs text-white/60">
                  Development mode: using a public STUN server. Production must set TURN_HOST.
                </p>
              )}
            </div>
          </section>
        )}

        {/* ---- Ended ---- */}
        {state.stage === "ended" && (
          <section className="mx-auto w-full max-w-3xl px-4 py-10 sm:px-6">
            <h1>Session over</h1>
            <p className="mt-2 text-ink-muted">{state.endedReason}</p>
            <p className="mt-6">
              {isHost ? (
                <a href="/admin/video-sessions/new">Create another session →</a>
              ) : (
                "You can close this window now."
              )}
            </p>
          </section>
        )}
      </SessionShell>
    </>
  )
}
